import { useState, useContext, useEffect, useCallback } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
	MessageSquare,
	Eye,
	EyeOff,
	Mail,
	Lock,
	LogIn,
	Check,
	ShieldCheck,
	ChevronLeft,
	ChevronRight,
	KeyRound,
	ArrowLeft,
	Loader2,
	AlertCircle,
	CheckCircle2,
	Clock,
	RefreshCw,
        Wrench,
        Wand2,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { Badge } from "../../components/ui/badge";
// Mesma regra de senha usada nos modais e no backend (helpers/ValidarSenha.ts):
// o reset público não pode aceitar senha mais fraca que o resto do sistema.
import { REGRA_SENHA, avaliarSenha, gerarSenhaForte } from "../../components/SenhaForte";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../../components/ui/dialog";
import { cn } from "../../lib/utils";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useWhiteLabel } from "../../context/WhiteLabel";
import { lerPreferencia, gravarPreferencia } from "../../services/preferencias";
import useMoedaAtiva, {
	formatarMoeda,
	moedaPlataforma,
} from "../../hooks/useMoedaAtiva";
import api from "../../services/api";

// Entrada do sistema.
//
// Duas metades: à esquerda o que o produto faz e quanto custa; à direita o
// formulário. Quem já é cliente olha só a direita — por isso o cartão fica do
// lado do polegar em telas grandes e é a única coisa que sobra no celular.
//
// A metade da esquerda existe porque esta URL também recebe quem ainda não
// comprou: o link do login circula, e uma tela só com dois campos não diz a
// essa pessoa o que ela está prestes a assinar.

const CHAVE_EMAIL = "login-email-lembrado";
const formatarDataHora = (valor) => {
        if (!valor) return "—";
        const data = new Date(valor);
        if (Number.isNaN(data.getTime())) return "—";
        return new Intl.DateTimeFormat("pt-BR", {
                dateStyle: "short",
                timeStyle: "short",
        }).format(data);
};

const montarMensagemRecuperacaoSenha = (maintenance) => {
        const fimFormatado = maintenance?.endsAt ? formatarDataHora(maintenance.endsAt) : "";
        return fimFormatado && fimFormatado !== "—"
                ? `Recuperação de senha indisponível durante a manutenção. Tente novamente após o período de manutenção (${fimFormatado}).`
                : "Recuperação de senha indisponível durante a manutenção. Tente novamente após o período de manutenção.";
};

const montarMensagemRetornoManutencao = (maintenance) => {
        const fimFormatado = maintenance?.endsAt ? formatarDataHora(maintenance.endsAt) : "";
        return fimFormatado && fimFormatado !== "—"
                ? `Sistema em manutenção. Retorno em ${fimFormatado}.`
                : "Sistema em manutenção. Retorno em breve.";
};

// Na moeda em que a plataforma cobra, não no real fixo: a vitrine é a
// primeira coisa que o cliente vê, e o preço errado ali não se desfaz depois.
const dinheiro = (valor) => formatarMoeda(valor, moedaPlataforma());

const Login = () => {
	// Puxa a moeda da plataforma; o formatador acima lê dela.
	useMoedaAtiva();
	const {
		appName,
		logoUrl,
		backgroundUrl,
		loginTitle,
		loginSubtitle,
		loginDescription,
		loginFeatures,
		loginHeroImageUrl,
		loginLogoUrl,
		defaultLoginTitle,
		defaultLoginSubtitle,
		defaultLoginDescription,
		defaultLoginFeatures,
	} = useWhiteLabel();
	const { handleLogin } = useContext(AuthContext);

	const TITULO_LOGIN = loginTitle?.trim() || defaultLoginTitle;
	const SUBTITULO_LOGIN = loginSubtitle?.trim() || defaultLoginSubtitle;
	const DESCRICAO_LOGIN = loginDescription?.trim() || defaultLoginDescription;
	const BENEFICIOS_LOGIN =
		Array.isArray(loginFeatures) &&
		loginFeatures.length > 0 &&
		loginFeatures.some((f) => f?.titulo?.trim() || f?.detalhe?.trim())
			? loginFeatures.filter(
					(f) => f?.titulo?.trim() || f?.detalhe?.trim()
			  )
			: defaultLoginFeatures;
	const LOGO_LOGIN = loginLogoUrl?.trim() || logoUrl;

	const [user, setUser] = useState({
		email: lerPreferencia(CHAVE_EMAIL, "") || "",
		password: "",
		twoFactorToken: "",
	});
	const [showPassword, setShowPassword] = useState(false);
	// Segunda etapa: só aparece depois que a senha conferiu e a conta tem
	// segundo fator ligado. Antes disso pedir o código seria dizer a quem
	// erra a senha se aquele e-mail existe e usa 2FA.
	const [pedindo2FA, setPedindo2FA] = useState(false);
	const [erro2FA, setErro2FA] = useState("");
	const [lembrar, setLembrar] = useState(
		Boolean(lerPreferencia(CHAVE_EMAIL, ""))
	);
	const [enviando, setEnviando] = useState(false);
        const [maintenance, setMaintenance] = useState(null);
        const [modalLoginManutencaoAberto, setModalLoginManutencaoAberto] = useState(false);

	const [planos, setPlanos] = useState([]);
	const [inicio, setInicio] = useState(0);

	// ─── Esqueci a senha ──────────────────────────────────────────────────────
	const [modalAberto, setModalAberto] = useState(false);
	const [etapa, setEtapa] = useState(1); // 1 email | 2 codigo | 3 senha nova
	const [emailReset, setEmailReset] = useState("");
	const [codigoReset, setCodigoReset] = useState("");
	const [senhaReset, setSenhaReset] = useState("");
	const [confirmarSenhaReset, setConfirmarSenhaReset] = useState("");
	const [resetToken, setResetToken] = useState("");
	const [enviandoReset, setEnviandoReset] = useState(false);
	const [msgReset, setMsgReset] = useState({ tipo: "", texto: "" }); // ok | err | info
	const [showResetPassword, setShowResetPassword] = useState(false);
	const [showConfirmarSenha, setShowConfirmarSenha] = useState(false);
	const [reenviarCooldown, setReenviarCooldown] = useState(0); // segundos
	const TOTAL_COOLDOWN_REENVIO = 60;
	const [diagReset, setDiagReset] = useState({
		mostrar: false,
		smtp: null, // array dos detalhes (smtpTrace)
		avisoDominioFake: "",
	});

	// A regra de senha (REGRA_SENHA + avaliarSenha) agora vem de
	// components/SenhaForte — o mesmo critério dos modais e do backend.

	const fecharModal = () => {
		setModalAberto(false);
		setTimeout(() => {
			setEtapa(1);
			setEmailReset("");
			setCodigoReset("");
			setSenhaReset("");
			setConfirmarSenhaReset("");
			setResetToken("");
			setMsgReset({ tipo: "", texto: "" });
			setDiagReset({ mostrar: false, smtp: null, avisoDominioFake: "" });
			setReenviarCooldown(0);
		}, 200);
	};

	useEffect(() => {
		if (reenviarCooldown <= 0) return;
		const id = setInterval(() => {
			setReenviarCooldown((v) => (v > 0 ? v - 1 : 0));
		}, 1000);
		return () => clearInterval(id);
	}, [reenviarCooldown]);

	const handleAbrirReset = () => {
                if (maintenance?.isActive) {
                        setMsgReset({
                                tipo: "info",
                                texto: montarMensagemRecuperacaoSenha(maintenance),
                        });
                        setDiagReset({ mostrar: false, smtp: null, avisoDominioFake: "" });
                        setEtapa(1);
                        setModalAberto(true);
                        return;
                }
		setEmailReset(user.email?.trim() || "");
		setMsgReset({ tipo: "", texto: "" });
		setDiagReset({ mostrar: false, smtp: null, avisoDominioFake: "" });
		setEtapa(1);
		setModalAberto(true);
	};

	const pedirCodigo = async () => {
		const email = emailReset.trim().toLowerCase();
		if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
			setMsgReset({ tipo: "err", texto: "E-mail inválido." });
			setDiagReset({ mostrar: false, smtp: null, avisoDominioFake: "" });
			return;
		}
		// Bloqueia clicks rápidos seguidos: inicia o cooldown de 60s no
		// frontend mesmo que a resposta do backend ainda esteja vindo. Se
		// der erro no cliente o usuário pode fechar o modal e abrir de novo.
		setReenviarCooldown(TOTAL_COOLDOWN_REENVIO);
		setEnviandoReset(true);
		setMsgReset({ tipo: "", texto: "" });
		setDiagReset({ mostrar: false, smtp: null, avisoDominioFake: "" });
		try {
			const { data } = await api.post("/public/forgot-password", { email });

			// Detector local de domínio fake sem precisar de SMTP_TRACE_ENABLED:
			// alerta o usuário de e-mails que certamente NÃO recebem (whaticket.com,
			// localhost, exemplo, teste). Isso resolve 90% dos "não chega" sem
			// enumerar nada, pois o usuário SABE que o email é dele.
			const dominioFake =
				/@(whaticket\.com|localhost|exemplo\.com|teste?\.com)$/i.test(email);

			// smtpTrace só virá preenchido se a VPS estiver com a env
			// SMTP_TRACE_ENABLED=true (modo debug).
			const trace = Array.isArray(data?.smtpTrace) ? data.smtpTrace : [];

			const avisosDominio = trace
				.map((t) => (t && typeof t.dominioFake ? "⚠️ " + String(t.to || email) + " é um domínio fake do seed." : null))
				.filter(Boolean);

			const avisoDominio =
				dominioFake && avisosDominio.length === 0
					? `⚠️ O e-mail "${email}" pertence a um domínio de exemplo (whaticket.com / localhost / exemplo.com / teste.com) e NÃO entrega mensagens reais. Para confirmar o recebimento, cadastre um e-mail real (seu gmail, outlook, domínio próprio, etc.) neste usuário.`
					: avisosDominio.join("\n") || "";

			setDiagReset({
				mostrar: trace.length > 0 || !!avisoDominio,
				smtp: trace.length > 0 ? trace : null,
				avisoDominioFake: avisoDominio,
			});

			setMsgReset({
				tipo: data?.ok ? "ok" : "info",
				texto:
					data?.message ||
					"Se o e-mail estiver cadastrado, enviamos o código.",
			});
			// UX desejada pelo usuário: sempre avança para a tela de inserir
			// o código, independentemente de o e-mail existir ou não. Isso
			// também ajuda a NÃO enumerar e-mails (mesma UI tanto para e-mail
			// cadastrado quanto para não cadastrado), mantendo segurança.
			setEtapa(2);
		} catch (err) {
                        if (err?.response?.data?.error === "ERR_SAAS_MAINTENANCE") {
                                const proximaManutencao =
                                        err?.response?.data?.details?.maintenance || maintenance;
                                setMaintenance(proximaManutencao || maintenance);
                                setMsgReset({
                                        tipo: "info",
                                        texto: montarMensagemRecuperacaoSenha(proximaManutencao),
                                });
                                return;
                        }
			setMsgReset({
				tipo: "info",
				texto:
					"Se o e-mail estiver cadastrado, enviamos o código. Verifique sua caixa de entrada ou spam.",
			});
		} finally {
			setEnviandoReset(false);
		}
	};

	const confirmarCodigo = async () => {
		const codigo = String(codigoReset || "")
			.trim()
			.replace(/[^0-9]/g, "");
		const email = emailReset.trim().toLowerCase();
		if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
			setMsgReset({ tipo: "err", texto: "E-mail inválido." });
			return;
		}
		if (codigo.length !== 6) {
			setMsgReset({ tipo: "err", texto: "Código deve ter 6 dígitos." });
			return;
		}
		setEnviandoReset(true);
		setMsgReset({ tipo: "", texto: "" });
		try {
			const { data } = await api.post("/public/confirm-reset-code", {
				email,
				code: codigo,
			});
			if (data?.ok && data?.token) {
				setResetToken(data.token);
				setEtapa(3);
				setMsgReset({
					tipo: "ok",
					texto: "Código confirmado. Crie sua nova senha.",
				});
			} else {
				setMsgReset({
					tipo: "err",
					texto: data?.erro || "Código inválido, expirado ou já usado.",
				});
			}
		} catch (err) {
                        if (err?.response?.data?.error === "ERR_SAAS_MAINTENANCE") {
                                const proximaManutencao =
                                        err?.response?.data?.details?.maintenance || maintenance;
                                setMaintenance(proximaManutencao || maintenance);
                                setMsgReset({
                                        tipo: "info",
                                        texto: montarMensagemRecuperacaoSenha(proximaManutencao),
                                });
                                return;
                        }
			setMsgReset({
				tipo: "err",
				texto:
					err?.response?.data?.erro ||
					"Código inválido, expirado ou já usado.",
			});
		} finally {
			setEnviandoReset(false);
		}
	};

	const salvarNovaSenha = async () => {
		const senha = senhaReset;
		const conf = confirmarSenhaReset;
		const analise = avaliarSenha(senha);
		if (!analise.ok) {
			setMsgReset({
				tipo: "err",
				texto: analise.erro,
			});
			return;
		}
		if (senha !== conf) {
			setMsgReset({ tipo: "err", texto: "As senhas digitadas não conferem." });
			return;
		}
		if (!resetToken) {
			setMsgReset({
				tipo: "err",
				texto: "Sessão expirada. Volte e peça um novo código.",
			});
			setEtapa(1);
			return;
		}
		setEnviandoReset(true);
		setMsgReset({ tipo: "", texto: "" });
		try {
			const { data } = await api.post("/public/reset-password", {
				token: resetToken,
				newPassword: senha,
			});
			if (data?.ok) {
				setMsgReset({
					tipo: "ok",
					texto:
						"Senha alterada com sucesso! Você já pode fazer login com a nova senha.",
				});
				setTimeout(() => {
					fecharModal();
					setUser((u) => ({ ...u, password: senha }));
				}, 1400);
			} else {
				setMsgReset({
					tipo: "err",
					texto: data?.erro || "Não foi possível alterar a senha.",
				});
			}
		} catch (err) {
                        if (err?.response?.data?.error === "ERR_SAAS_MAINTENANCE") {
                                const proximaManutencao =
                                        err?.response?.data?.details?.maintenance || maintenance;
                                setMaintenance(proximaManutencao || maintenance);
                                setMsgReset({
                                        tipo: "info",
                                        texto: montarMensagemRecuperacaoSenha(proximaManutencao),
                                });
                                return;
                        }
			setMsgReset({
				tipo: "err",
				texto:
					err?.response?.data?.erro ||
					"Não foi possível alterar a senha. Tente novamente.",
			});
		} finally {
			setEnviandoReset(false);
		}
	};

	// Gera a senha e já preenche a confirmação: a pessoa vê na tela, copia e
	// segue. Sem isso, o reset exigia inventar uma senha forte de cabeça.
	const gerarSenhaReset = () => {
		const nova = gerarSenhaForte();
		setSenhaReset(nova);
		setConfirmarSenhaReset(nova);
		setShowResetPassword(true);
		setShowConfirmarSenha(true);
		setMsgReset({
			tipo: "info",
			texto: "Senha forte gerada. Copie agora: ela não aparece de novo.",
		});
	};

	// Os planos são os mesmos que a tela de cadastro vende. Buscar da API em
	// vez de escrever na mão evita a tela anunciar um preço que já mudou.
	useEffect(() => {
		let vivo = true;

		api
			.get("/auth/public-plans")
			.then(({ data }) => {
				if (!vivo) return;
				// Do mais barato para o mais caro: é a ordem em que se decide.
				const ordenados = [...(data || [])].sort(
					(a, b) => Number(a.value) - Number(b.value)
				);
				setPlanos(ordenados);
			})
			.catch(() => {
				// Sem planos a tela continua servindo para entrar, que é o
				// principal. Não vale mostrar erro a quem só quer fazer login.
			});

		return () => {
			vivo = false;
		};
	}, []);

        useEffect(() => {
                let vivo = true;

                api
                        .get("/auth/maintenance")
                        .then(({ data }) => {
                                if (!vivo) return;
                                setMaintenance(data || null);
                        })
                        .catch(() => {
                                if (!vivo) return;
                                setMaintenance(null);
                        });

                return () => {
                        vivo = false;
                };
        }, []);

	const handleChangeInput = (e) => {
		setUser({ ...user, [e.target.name]: e.target.value });
	};

	const handlSubmit = async (e) => {
		e.preventDefault();
		setEnviando(true);

		// "Lembrar" guarda só o e-mail, nunca a senha: poupa a digitação de
		// quem entra todo dia sem deixar credencial no navegador.
		gravarPreferencia(CHAVE_EMAIL, lembrar ? user.email : "");

		try {
			await handleLogin(user);
		} catch (err) {
			const codigo = err?.precisa2FA
				? "ERR_2FA_REQUIRED"
				: err?.response?.data?.error;

			if (codigo === "ERR_2FA_REQUIRED") {
				// Senha certa, falta o segundo fator: abre a etapa do código.
				setPedindo2FA(true);
				setErro2FA("");
				setUser((atual) => ({ ...atual, twoFactorToken: "" }));
			} else if (codigo === "ERR_INVALID_2FA_TOKEN") {
				// Código errado ou já expirado. A etapa continua aberta e o
				// campo limpa, senão a pessoa reenvia o mesmo código vencido.
				setErro2FA("Código inválido. Use o código que o app mostra agora.");
				setUser((atual) => ({ ...atual, twoFactorToken: "" }));
                        } else if (codigo === "ERR_SAAS_MAINTENANCE") {
                                setPedindo2FA(false);
                                setErro2FA("");
                                setMaintenance(err?.response?.data?.details?.maintenance || maintenance);
                                setModalLoginManutencaoAberto(true);
			} else {
				// Qualquer outra falha volta para o começo: e-mail e senha.
				setPedindo2FA(false);
				setErro2FA("");
			}
		} finally {
			setEnviando(false);
		}
	};

	const porPagina = 3;
	const podeVoltar = inicio > 0;
	const podeAvancar = inicio + porPagina < planos.length;

	const mover = useCallback(
		(passo) => {
			setInicio((atual) => {
				const novo = atual + passo * porPagina;
				if (novo < 0) return 0;
				if (novo >= planos.length) return atual;
				return novo;
			});
		},
		[planos.length]
	);

        const visiveis = planos.slice(inicio, inicio + porPagina);
        const manutencaoAtiva = maintenance?.isActive;
        const manutencaoProgramada = maintenance?.isScheduled;
        const mostrarAvisoManutencao = manutencaoAtiva || manutencaoProgramada;
        const avisoRecuperacaoSenha = montarMensagemRecuperacaoSenha(maintenance);
        const avisoRetornoManutencao = montarMensagemRetornoManutencao(maintenance);
        const tituloPrincipal = manutencaoAtiva
                ? "Sistema em manutenção."
                : TITULO_LOGIN;
        const subtituloPrincipal = manutencaoAtiva
                ? "Voltamos em breve."
                : SUBTITULO_LOGIN;
        const descricaoPrincipal = manutencaoAtiva
                ? maintenance?.message?.trim() ||
                  "Estamos realizando uma atualização programada para melhorar a estabilidade e a performance do sistema. Novos cadastros ficam temporariamente indisponíveis durante esta janela."
                : DESCRICAO_LOGIN;
        const itensManutencao = [
                {
                        titulo: "Janela programada",
                        detalhe: `${formatarDataHora(maintenance?.startsAt)}${
                                maintenance?.endsAt ? ` ate ${formatarDataHora(maintenance?.endsAt)}` : ""
                        }`,
                },
                {
                        titulo: "Retorno previsto",
                        detalhe: maintenance?.endsAt
                                ? `O acesso completo volta automaticamente após ${formatarDataHora(
                                          maintenance?.endsAt
                                  )}.`
                                : "Assim que a janela for encerrada, o acesso completo volta automaticamente.",
                },
        ];
        const descricaoManutencaoCard = manutencaoAtiva
                ? "Atualização programada em andamento."
                : "Esta janela será aplicada automaticamente no horário programado.";
        const motivoManutencao =
                maintenance?.message?.trim() || "Atualização programada em andamento.";

	return (
		// A tela inteira é escura por baixo do conteúdo, com ou sem foto: o
		// cartão claro precisa de contraste para não flutuar no nada.
		<div className="relative min-h-dvh w-full overflow-hidden bg-slate-950">
			{backgroundUrl ? (
				<>
					<img
						src={backgroundUrl}
						alt=""
						aria-hidden="true"
						className="absolute inset-0 h-full w-full object-cover"
					/>
					{/* Véu por cima da foto: sem ele, texto branco sobre uma
					    imagem clara fica ilegível — e a foto é escolhida pelo
					    cliente, então não dá para contar com ela ser escura. */}
					<div className="absolute inset-0 bg-slate-950/80" />
				</>
			) : (
				<div
					className="absolute inset-0 opacity-70"
					style={{
						backgroundImage:
							"radial-gradient(ellipse 70% 60% at 15% -10%, rgba(37,99,235,0.45), transparent 60%), radial-gradient(ellipse 60% 50% at 100% 110%, rgba(16,185,129,0.35), transparent 60%)",
					}}
				/>
			)}

                        <div className="relative z-10 mx-auto flex min-h-dvh max-w-7xl flex-col items-center justify-center gap-8 px-4 py-6 lg:flex-row lg:items-center lg:gap-10 lg:px-8 lg:py-6">
				{/* Apresentação — some no celular, onde a tela toda é o
				    formulário e rolar até ele seria um estorvo para quem só
				    quer entrar. */}
                                <div className="hidden min-w-0 flex-1 lg:block">
					{loginHeroImageUrl?.trim() ? (
						<div className="mb-8 flex">
							<img
								src={loginHeroImageUrl}
								alt=""
								className="max-h-56 w-auto object-contain drop-shadow-2xl"
							/>
						</div>
					) : null}

                                        {manutencaoAtiva ? (
                                                <div className="max-w-[40rem]">
                                                        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-amber-300/30 bg-amber-400/10 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-200 backdrop-blur-sm">
                                                                <Wrench className="h-3.5 w-3.5" />
                                                                Manutenção programada
                                                        </div>

                                                        <h1 className="max-w-[20rem] text-[clamp(3rem,5vw,4.5rem)] font-bold leading-[0.98] tracking-tight text-white">
                                                                {tituloPrincipal}
                                                        </h1>

                                                        {subtituloPrincipal ? (
                                                                <p className="mt-3 text-[clamp(2.1rem,3.8vw,3.25rem)] font-semibold leading-[1.02] tracking-tight text-amber-300">
                                                                        {subtituloPrincipal}
                                                                </p>
                                                        ) : null}

                                                        <p className="mt-5 max-w-[34rem] text-[15px] leading-7 text-slate-300">
                                                                {descricaoPrincipal}
                                                        </p>
                                                </div>
                                        ) : (
                                                <>
                                                        <h1 className="text-4xl font-bold leading-tight tracking-tight text-white">
                                                                {tituloPrincipal}
                                                                {subtituloPrincipal ? (
                                                                        <>
                                                                                <br />
                                                                                <span className="text-emerald-400">{subtituloPrincipal}</span>
                                                                        </>
                                                                ) : null}
                                                        </h1>

                                                        <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-slate-300">
                                                                {descricaoPrincipal}
                                                        </p>
                                                </>
                                        )}

                                        <ul className={cn("mt-7", manutencaoAtiva ? "max-w-[38rem] space-y-4" : "space-y-4")}>
                                                {(manutencaoAtiva ? itensManutencao : BENEFICIOS_LOGIN).map((item, i) => {
							const titulo = item?.titulo || "";
							const detalhe = item?.detalhe || "";
							const key = titulo + i;
							return (
								<li key={key} className="flex items-start gap-3">
                                                                        <span
                                                                                className={cn(
                                                                                        "mt-0.5 flex shrink-0 items-center justify-center rounded-full ring-1",
                                                                                        manutencaoAtiva
                                                                                                ? "h-7 w-7 bg-amber-500/15 ring-amber-300/30"
                                                                                                : "bg-emerald-500/20 ring-emerald-400/40"
                                                                                )}
                                                                        >
                                                                                <Check
                                                                                        className={cn(
                                                                                                manutencaoAtiva ? "h-3.5 w-3.5" : "h-3 w-3",
                                                                                                manutencaoAtiva ? "text-amber-300" : "text-emerald-400"
                                                                                        )}
                                                                                />
									</span>
									<div className="min-w-0">
										{titulo ? (
                                                                                        <p className={cn("font-semibold text-white", manutencaoAtiva ? "text-[1rem]" : "text-sm")}>{titulo}</p>
										) : null}
										{detalhe ? (
                                                                                        <p className={cn("mt-1 leading-relaxed text-slate-400", manutencaoAtiva ? "max-w-[36rem] text-[14px]" : "text-sm")}>
												{detalhe}
											</p>
										) : null}
									</div>
								</li>
							);
						})}
					</ul>

                                        {planos.length > 0 && !manutencaoAtiva && (
						<div className="mt-10">
							<div className="flex items-center justify-between">
								<p className="text-xs font-semibold uppercase tracking-widest text-slate-400">
									Planos
								</p>

								{planos.length > porPagina && (
									<div className="flex items-center gap-1.5">
										<button
											type="button"
											aria-label="Planos anteriores"
											disabled={!podeVoltar}
											onClick={() => mover(-1)}
											className="flex h-8 w-8 items-center justify-center rounded-full text-slate-300 ring-1 ring-white/20 transition-colors enabled:hover:bg-white/10 disabled:opacity-30"
										>
											<ChevronLeft className="h-4 w-4" />
										</button>
										<button
											type="button"
											aria-label="Próximos planos"
											disabled={!podeAvancar}
											onClick={() => mover(1)}
											className="flex h-8 w-8 items-center justify-center rounded-full text-slate-300 ring-1 ring-white/20 transition-colors enabled:hover:bg-white/10 disabled:opacity-30"
										>
											<ChevronRight className="h-4 w-4" />
										</button>
									</div>
								)}
							</div>

							<div className="mt-3 grid grid-cols-3 gap-3">
								{visiveis.map((plano, i) => {
									// O mais barato da primeira página é o que a maioria
									// contrata; destacá-lo poupa a comparação.
									const destaque = inicio === 0 && i === 0;

									return (
										<div
											key={plano.id}
											className={cn(
												"relative rounded-xl p-4 ring-1 backdrop-blur-sm",
												destaque
													? "bg-emerald-500/10 ring-emerald-400/40"
													: "bg-white/5 ring-white/10"
											)}
										>
											{destaque && (
												<span className="absolute -top-2 left-4 rounded-full bg-emerald-400 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-950">
													Mais popular
												</span>
											)}

											<p className="truncate text-[11px] font-semibold uppercase tracking-wide text-slate-400">
												{plano.name}
											</p>
											<p className="mt-1 text-xl font-bold tabular-nums text-white">
												{dinheiro(plano.value)}
											</p>
											<p className="text-[11px] text-slate-400">por mês</p>

											<ul className="mt-3 space-y-1.5 text-[12px] text-slate-300">
												<li className="flex items-center gap-1.5">
													<Check className="h-3 w-3 shrink-0 text-emerald-400" />
													{plano.users} atendente(s)
												</li>
												<li className="flex items-center gap-1.5">
													<Check className="h-3 w-3 shrink-0 text-emerald-400" />
													{plano.connections} conexão(ões)
												</li>
												<li className="flex items-center gap-1.5">
													<Check className="h-3 w-3 shrink-0 text-emerald-400" />
													{plano.queues} fila(s)
												</li>
											</ul>
										</div>
									);
								})}
							</div>
						</div>
					)}
				</div>

				{/* Formulário */}
                                <div className="w-full max-w-md shrink-0">
                                        <div
                                                className={cn(
                                                        "rounded-2xl p-6 shadow-2xl ring-1 sm:p-7",
                                                        manutencaoAtiva
                                                                ? "border border-amber-200/70 bg-gradient-to-b from-amber-50 via-white to-white ring-amber-200/60"
                                                                : "bg-card ring-black/5"
                                                )}
                                        >
						<div className="flex justify-center">
							{LOGO_LOGIN ? (
								<img
									src={LOGO_LOGIN}
									alt={appName}
									className="h-12 max-w-[180px] object-contain"
								/>
							) : (
								<div className="flex items-center gap-2.5">
									<div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary">
										<MessageSquare className="h-6 w-6 text-primary-foreground" />
									</div>
									<span className="text-lg font-bold tracking-tight">
										{appName}
									</span>
								</div>
							)}
						</div>

                                                <div className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground">
                                                        {manutencaoAtiva ? (
                                                                <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-amber-200 bg-amber-50/80 px-3 py-1.5 text-[11px] font-medium leading-5 text-amber-800 shadow-sm">
                                                                        <Wrench className="h-3.5 w-3.5 shrink-0" />
                                                                        <span className="truncate">{avisoRetornoManutencao}</span>
                                                                </div>
                                                        ) : (
                                                                <>
                                                                        <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
                                                                        Área segura
                                                                </>
                                                        )}
                                                </div>

                                                {mostrarAvisoManutencao && !manutencaoAtiva && (
                                                        <div
                                                                className={cn(
                                                                        "mt-4 overflow-hidden rounded-2xl border text-sm",
                                                                        "border-sky-300/60 bg-sky-50 text-sky-900 dark:border-sky-700/40 dark:bg-sky-950/30 dark:text-sky-100"
                                                                )}
                                                        >
                                                                <div
                                                                        className={cn(
                                                                                "px-4 py-4",
                                                                                "bg-sky-100/70"
                                                                        )}
                                                                >
                                                                        <div className="flex items-start gap-3">
                                                                                <div
                                                                                        className={cn(
                                                                                                "flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl shadow-sm ring-1",
                                                                                                manutencaoAtiva
                                                                                                        ? "bg-white/95 text-amber-700 ring-amber-200"
                                                                                                        : "bg-white text-sky-700 ring-sky-200"
                                                                                        )}
                                                                                >
                                                                                        {manutencaoAtiva ? (
                                                                                                <Wrench className="h-5 w-5" />
                                                                                        ) : (
                                                                                                <Clock className="h-5 w-5" />
                                                                                        )}
                                                                                </div>
                                                                                <div className="min-w-0">
                                                                                        <p className="text-[1rem] font-semibold tracking-tight">
                                                                                                Manutenção programada
                                                                                        </p>
                                                                                        <p className="mt-1 text-[13.5px] leading-6 text-sky-900/80">
                                                                                                {descricaoManutencaoCard}
                                                                                        </p>
                                                                                </div>
                                                                        </div>
                                                                </div>

                                                                {!manutencaoAtiva && (
                                                                        <div className="px-4 pb-4">
                                                                                <div className="grid gap-2 text-xs sm:grid-cols-2">
                                                                                        <div className="rounded-xl bg-white/80 px-3 py-3 shadow-sm ring-1 ring-amber-100">
                                                                                                <span className="font-medium">Início</span>
                                                                                                <p className="mt-1 text-sm font-semibold">
                                                                                                        {formatarDataHora(maintenance?.startsAt)}
                                                                                                </p>
                                                                                        </div>
                                                                                        <div className="rounded-xl bg-white/80 px-3 py-3 shadow-sm ring-1 ring-amber-100">
                                                                                                <span className="font-medium">Fim</span>
                                                                                                <p className="mt-1 text-sm font-semibold">
                                                                                                        {formatarDataHora(maintenance?.endsAt)}
                                                                                                </p>
                                                                                        </div>
                                                                                </div>
                                                                        </div>
                                                                )}
                                                        </div>
                                                )}

                                                <h2 className="mt-4 text-2xl font-bold tracking-tight">Entrar</h2>
                                                {!manutencaoAtiva && (
                                                        <p className="mt-1 text-sm text-muted-foreground">
                                                                Use suas credenciais para acessar o sistema.
                                                        </p>
                                                )}

						{/* Duas abas, e a de cadastro leva para outra tela. Fica
						    como aba, e não como link no rodapé, porque quem chega
						    sem conta precisa achar o caminho de primeira. */}
                                                <div className="mt-4 grid grid-cols-2 gap-1 rounded-lg bg-muted p-1">
							<span
								aria-current="page"
								className="rounded-md bg-background px-3 py-2 text-center text-sm font-semibold shadow-sm"
							>
								Entrar
							</span>
                                                        {manutencaoAtiva ? (
                                                                <span className="rounded-md px-3 py-2 text-center text-sm font-medium text-muted-foreground/70">
                                                                        Cadastre-se indisponível
                                                                </span>
                                                        ) : (
                                                                <RouterLink
                                                                        to="/signup"
                                                                        className="rounded-md px-3 py-2 text-center text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                                                                >
                                                                        Cadastre-se
                                                                </RouterLink>
                                                        )}
						</div>

                                                <form onSubmit={handlSubmit} className="mt-4 space-y-4" noValidate>
							<div className="space-y-1.5">
								<Label htmlFor="email">{i18n.t("login.form.email")}</Label>
								<div className="relative">
									<Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
									<Input
										id="email"
										name="email"
										type="email"
										autoComplete="email"
										autoFocus
										required
										value={user.email}
										onChange={handleChangeInput}
										placeholder="voce@exemplo.com"
										className="h-11 pl-10"
									/>
								</div>
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="password">
									{i18n.t("login.form.password")}
								</Label>
								<div className="relative">
									<Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
									<Input
										id="password"
										name="password"
										type={showPassword ? "text" : "password"}
										autoComplete="current-password"
										required
										value={user.password}
										onChange={handleChangeInput}
										placeholder="Sua senha"
										className="h-11 pl-10 pr-11"
									/>
									<button
										type="button"
										tabIndex={-1}
										aria-label={
											showPassword ? "Esconder a senha" : "Mostrar a senha"
										}
										onClick={() => setShowPassword((v) => !v)}
										className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
									>
										{showPassword ? (
											<EyeOff className="h-4 w-4" />
										) : (
											<Eye className="h-4 w-4" />
										)}
									</button>
								</div>
							</div>

							{pedindo2FA && (
								<div className="space-y-1.5">
									<Label htmlFor="twoFactorToken">
										Código de verificação
									</Label>
									<div className="relative">
										<ShieldCheck className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
										<Input
											id="twoFactorToken"
											name="twoFactorToken"
											type="text"
											inputMode="numeric"
											autoComplete="one-time-code"
											maxLength={6}
											autoFocus
											required
											value={user.twoFactorToken}
											onChange={handleChangeInput}
											placeholder="000000"
											className="h-11 pl-10 tracking-[0.35em]"
										/>
									</div>
									<p
										className={cn(
											"text-xs",
											erro2FA ? "text-destructive" : "text-muted-foreground"
										)}
									>
										{erro2FA ||
											"Abra o aplicativo de autenticação e digite os seis dígitos."}
									</p>
								</div>
							)}

							<div className="flex items-center justify-between gap-2">
								<div className="flex items-center gap-2">
									<Checkbox
										id="lembrar"
										checked={lembrar}
										onCheckedChange={(v) => setLembrar(Boolean(v))}
									/>
									<Label
										htmlFor="lembrar"
										className="cursor-pointer text-sm font-normal text-muted-foreground"
									>
										Lembrar meu e-mail
									</Label>
								</div>
                                                                <button
                                                                        type="button"
                                                                        onClick={handleAbrirReset}
                                                                        className="text-sm font-medium text-primary underline-offset-4 transition-colors hover:underline hover:text-primary/80"
                                                                >
                                                                        Esqueci a senha
                                                                </button>
							</div>

							<Button
								type="submit"
								disabled={enviando}
								className="h-11 w-full text-[15px] font-semibold"
							>
								<LogIn className="h-4 w-4" />
								{i18n.t("login.buttons.submit")}
							</Button>
						</form>

						<p className="mt-5 text-center text-xs leading-relaxed text-muted-foreground">
							Ao entrar, você concorda com o tratamento dos seus dados conforme
							a LGPD.
						</p>
					</div>

					{/* No celular a apresentação não aparece; este atalho é o que
					    resta para quem chegou aqui sem conta. */}
                                        <p className="mt-5 text-center text-sm text-slate-300 lg:hidden">
                                                {manutencaoAtiva ? (
                                                        <span>Cadastro temporariamente indisponível durante a manutenção.</span>
                                                ) : (
                                                        <>
                                                                Ainda não tem conta?{" "}
                                                                <RouterLink
                                                                        to="/signup"
                                                                        className="font-semibold text-white underline underline-offset-4"
                                                                >
                                                                        Criar agora
                                                                </RouterLink>
                                                        </>
                                                )}
                                        </p>
				</div>
			</div>

			{/* Modal Esqueci a senha */}
                        <Dialog open={modalLoginManutencaoAberto} onOpenChange={setModalLoginManutencaoAberto}>
                                <DialogContent className="w-[92vw] max-w-md p-0 overflow-hidden">
                                        <DialogHeader className="space-y-1.5 border-b px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                                                                <Wrench className="h-4.5 w-4.5" />
                                                        </div>
                                                        <div>
                                                                <DialogTitle className="text-lg font-bold">
                                                                        Sistema em manutenção
                                                                </DialogTitle>
                                                                <DialogDescription className="text-sm">
                                                                        {maintenance?.endsAt
                                                                                ? `Retorno em ${formatarDataHora(maintenance?.endsAt)}.`
                                                                                : "Retorno em breve."}
                                                                </DialogDescription>
                                                        </div>
                                                </div>
                                        </DialogHeader>

                                        <div className="px-6 py-5">
                                                <div className="rounded-2xl border border-amber-200/80 bg-gradient-to-br from-amber-50 via-white to-amber-50/70 p-4 text-sm text-amber-950 shadow-[0_14px_30px_rgba(180,83,9,0.10)]">
                                                        <p className="text-[14px] leading-7 text-amber-950">
                                                                {avisoRetornoManutencao}
                                                        </p>
                                                </div>
                                        </div>

                                        <DialogFooter className="border-t bg-muted/40 px-6 py-3">
                                                <Button
                                                        type="button"
                                                        variant="ghost"
                                                        onClick={() => setModalLoginManutencaoAberto(false)}
                                                >
                                                        Fechar
                                                </Button>
                                        </DialogFooter>
                                </DialogContent>
                        </Dialog>

                        <Dialog open={modalAberto} onOpenChange={(v) => !v && fecharModal()}>
                                <DialogContent className={cn("w-[92vw] p-0 overflow-hidden", manutencaoAtiva ? "max-w-lg" : "max-w-md")}>
					<DialogHeader className="space-y-1.5 border-b px-6 py-4">
						<div className="flex items-center gap-2">
							<div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
								<KeyRound className="h-4.5 w-4.5" />
							</div>
							<div>
								<DialogTitle className="text-lg font-bold">
									Redefinir senha
								</DialogTitle>
								<DialogDescription className="text-xs">
                                                                        {manutencaoAtiva
                                                                                ? "Indisponível durante a manutenção programada."
                                                                                : `Passo ${etapa} de 3 — ${
                                                                                          etapa === 1
                                                                                                  ? "enviaremos um código por e-mail"
                                                                                                  : etapa === 2
                                                                                                  ? "confirme o código de 6 dígitos"
                                                                                                  : "crie sua nova senha"
                                                                                  }`}
								</DialogDescription>
							</div>
						</div>
					</DialogHeader>

					<div className="space-y-4 px-6 py-5">
                                                {manutencaoAtiva ? (
                                                        <div className="rounded-2xl border border-amber-200/80 bg-gradient-to-br from-amber-50 via-white to-amber-50/70 p-5 text-sm text-amber-950 shadow-[0_14px_30px_rgba(180,83,9,0.10)]">
                                                                <div className="flex items-start gap-4">
                                                                        <div className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-amber-700 shadow-sm ring-1 ring-amber-200">
                                                                                <Wrench className="h-4 w-4" />
                                                                        </div>
                                                                        <div className="space-y-3">
                                                                                <div className="space-y-1">
                                                                                        <p className="text-[1.1rem] font-semibold tracking-tight">
                                                                                                Recuperação de senha temporariamente desabilitada
                                                                                        </p>
                                                                                        <p className="text-[14px] leading-6 text-amber-900/80">
                                                                                                {motivoManutencao}
                                                                                        </p>
                                                                                </div>
                                                                                <p className="text-[14px] leading-7 text-amber-950">
                                                                                        {avisoRecuperacaoSenha}
                                                                                </p>
                                                                                <div className="rounded-xl bg-white/90 px-4 py-3 text-xs shadow-sm ring-1 ring-amber-100">
                                                                                        <span className="font-medium uppercase tracking-[0.14em] text-amber-700">
                                                                                                Janela da manutenção
                                                                                        </span>
                                                                                        <p className="mt-2 text-[15px] font-semibold leading-6 text-amber-950">
                                                                                                {formatarDataHora(maintenance?.startsAt)}
                                                                                                {maintenance?.endsAt
                                                                                                        ? ` ate ${formatarDataHora(maintenance?.endsAt)}`
                                                                                                        : ""}
                                                                                        </p>
                                                                                </div>
                                                                        </div>
                                                                </div>
                                                        </div>
                                                ) : (
                                                        <>
						{msgReset.texto ? (
							<div
								className={cn(
									"flex items-start gap-2 rounded-lg border p-3 text-sm",
									msgReset.tipo === "ok"
										? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800/40 dark:bg-emerald-950/40 dark:text-emerald-300"
										: msgReset.tipo === "err"
										? "border-red-200 bg-red-50 text-red-700 dark:border-red-800/40 dark:bg-red-950/40 dark:text-red-300"
										: "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-800/40 dark:bg-sky-950/40 dark:text-sky-300"
								)}
							>
								{msgReset.tipo === "ok" ? (
									<CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
								) : msgReset.tipo === "err" ? (
									<AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
								) : (
									<ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
								)}
								<span>{msgReset.texto}</span>
							</div>
						) : null}

						{diagReset.mostrar && (
							<div className="rounded-lg border border-amber-300/60 bg-amber-50 text-amber-800 dark:border-amber-600/30 dark:bg-amber-950/30 dark:text-amber-200 text-xs space-y-2 p-3">
								{diagReset.avisoDominioFake ? (
									<div className="whitespace-pre-line leading-snug break-words">
										{diagReset.avisoDominioFake}
									</div>
								) : null}

								{Array.isArray(diagReset.smtp) && diagReset.smtp.length > 0 && (
									<div className="space-y-2 pt-1">
										<div className="font-semibold text-[11px] uppercase tracking-wide opacity-80">
											Diagnóstico SMTP (modo trace)
										</div>
										{diagReset.smtp.map((t, idx) => {
											const linhas = [
												t.ok === true
													? "Status: enviado"
													: t.ok === false
													? "Status: falhou"
													: null,
												typeof t.tempoMs === "number"
													? `Tempo: ${t.tempoMs} ms`
													: null,
												t.verifyOk === true
													? "Verify: OK"
													: t.verifyOk === false
													? "Verify: FALHOU"
													: null,
												Array.isArray(t.accepted) && t.accepted.length > 0
													? `Aceitos: ${t.accepted.join(", ")}`
													: null,
												Array.isArray(t.rejected) && t.rejected.length > 0
													? `Rejeitados: ${t.rejected.join(", ")}`
													: null,
												t.messageId
													? `ID: ${String(t.messageId).slice(0, 80)}`
													: null,
												t.smtpResponse
													? `SMTP: ${String(t.smtpResponse).slice(0, 160)}`
													: null,
												t.erro
													? `Erro: ${String(t.erro).slice(0, 200)}`
													: null,
												t.verifyErro
													? `Erro verify(): ${String(t.verifyErro).slice(0, 160)}`
													: null,
												t.host
													? `Servidor: ${t.host}:${t.port ?? "?"}${
															t.user ? " · " + String(t.user).slice(0, 60) : ""
													  }`
													: null,
												t.fase && t.resultado
													? `${String(t.fase)} → ${String(t.resultado)}`
													: null,
											].filter(Boolean);
											if (linhas.length === 0) return null;
											return (
												<div
													key={idx}
													className="rounded-md border border-amber-300/40 bg-white/70 dark:bg-black/20 p-2 space-y-0.5 break-words whitespace-pre-line"
												>
													{linhas.map((l, i) => (
														<div key={i} className="leading-snug">{l}</div>
													))}
												</div>
											);
										})}
									</div>
								)}

								<div className="pt-1 text-[10px] opacity-75 leading-snug">
									Caso o sistema esteja sem SMTP_TRACE_ENABLED, você pode ver o mesmo
									diagnóstico rodando no servidor:{" "}
									<code className="font-mono">
										docker logs atendimento-api-1 --tail 80 | grep
										PASSWORD-RESET
									</code>
									.
								</div>
							</div>
						)}

                                                {etapa === 1 && (
							<div className="space-y-1.5">
								<Label htmlFor="reset-email">E-mail cadastrado</Label>
								<Input
									id="reset-email"
									type="email"
									autoComplete="email"
									autoFocus
									value={emailReset}
									onChange={(e) => setEmailReset(e.target.value)}
									placeholder="voce@exemplo.com"
									className="h-11"
									onKeyDown={(e) => {
										if (e.key === "Enter") pedirCodigo();
									}}
								/>
								<p className="text-xs text-muted-foreground">
									Se o e-mail existir, enviaremos um código de 6 dígitos para
									ele.
								</p>
							</div>
						)}

						{etapa === 2 && (
							<div className="space-y-1.5">
								<Label htmlFor="reset-codigo">
									Código de 6 dígitos (enviado para{" "}
									<span className="font-medium">{emailReset}</span>)
								</Label>
								<Input
									id="reset-codigo"
									type="text"
									inputMode="numeric"
									autoComplete="one-time-code"
									maxLength={6}
									autoFocus
									value={codigoReset}
									onChange={(e) =>
										setCodigoReset(
											e.target.value
												.replace(/[^0-9]/g, "")
												.slice(0, 6)
										)
									}
									placeholder="000000"
									className="h-11 text-center tracking-[0.5em] font-semibold"
									onKeyDown={(e) => {
										if (e.key === "Enter") confirmarCodigo();
									}}
								/>
								<div className="flex items-center justify-between pt-1">
									<button
										type="button"
										variant="link"
										onClick={() => setEtapa(1)}
										className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
									>
										<ArrowLeft className="h-3.5 w-3.5" /> E-mail errado?
									</button>
									{reenviarCooldown > 0 ? (
										<Badge
											variant="outline"
											className="font-normal text-[11px] h-6"
										>
											<Clock className="h-3 w-3 mr-1 opacity-75" />
											Aguarde {reenviarCooldown}s para reenviar
										</Badge>
									) : (
										<button
											type="button"
											onClick={pedirCodigo}
											disabled={enviandoReset}
											className="text-xs font-medium text-primary hover:underline inline-flex items-center gap-1"
										>
											<RefreshCw
												className={`h-3.5 w-3.5 ${
													enviandoReset ? "animate-spin" : ""
												}`}
											/>
											Reenviar código
										</button>
									)}
								</div>
							</div>
						)}

						{etapa === 3 && (
							(() => {
								const analise = avaliarSenha(senhaReset);
								const conf = String(confirmarSenhaReset || "");
								const senha = String(senhaReset || "");
								const confere =
									senha.length > 0 && conf.length > 0 && senha === conf;
								const naoConfere =
									senha.length > 0 && conf.length > 0 && senha !== conf;
								const corForca =
									analise.forca === "segura"
										? "bg-emerald-500"
										: analise.forca === "moderada"
										? "bg-amber-500"
										: "bg-red-500";
								const textoForca =
									analise.forca === "segura"
										? "text-emerald-700 dark:text-emerald-300"
										: analise.forca === "moderada"
										? "text-amber-700 dark:text-amber-300"
										: "text-red-700 dark:text-red-300";
								const regraItems = [
									{ k: "len", ok: analise.regras.len, label: `${REGRA_SENHA.minLength}+ caracteres` },
									{ k: "lower", ok: analise.regras.lower, label: "Letra minúscula" },
									{ k: "upper", ok: analise.regras.upper, label: "Letra MAIÚSCULA" },
									{ k: "num", ok: analise.regras.num, label: "Número (0-9)" },
									{ k: "special", ok: analise.regras.special, label: "Caractere especial (! @ # $ ...)" },
								];
								return (
									<div className="space-y-3">
										<div className="space-y-1.5">
											<Label htmlFor="reset-senha">Nova senha</Label>
											<div className="relative">
												<Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
												<Input
													id="reset-senha"
													type={showResetPassword ? "text" : "password"}
													autoComplete="new-password"
													autoFocus
													minLength={REGRA_SENHA.minLength}
													maxLength={REGRA_SENHA.maxLength}
													value={senhaReset}
													onChange={(e) => setSenhaReset(e.target.value)}
													placeholder="Senha forte com maiúscula, número e !"
													className="h-11 pl-10 pr-11"
												/>
												<button
													type="button"
													tabIndex={-1}
													aria-label={showResetPassword ? "Esconder a senha" : "Mostrar a senha"}
													onClick={() => setShowResetPassword((v) => !v)}
													className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
												>
													{showResetPassword ? (
														<EyeOff className="h-4 w-4" />
													) : (
														<Eye className="h-4 w-4" />
													)}
												</button>
											</div>
										</div>

										{/* Barra de força + label */}
										<div className="space-y-1.5">
											<div className="flex items-center justify-between gap-2">
												<span className="text-[11px] font-medium text-muted-foreground">
													Força da senha
												</span>
												{analise.score > 0 && (
													<span
														className={cn(
															"text-[11px] font-semibold capitalize",
															textoForca
														)}
													>
														{analise.forca === "fraca"
															? "fraca"
															: analise.forca === "moderada"
															? "moderada"
															: "segura"}
													</span>
												)}
											</div>
											<div className="h-2 w-full overflow-hidden rounded-full bg-muted">
												<div
													className={cn(
														"h-full rounded-full transition-all duration-200",
														corForca
													)}
													style={{ width: `${analise.pct}%` }}
												/>
											</div>
										</div>

										{/* Atalho para quem não quer inventar senha: gera, mostra
										    e já preenche a confirmação. */}
										<Button
											type="button"
											variant="outline"
											size="sm"
											onClick={gerarSenhaReset}
											className="w-full"
										>
											<Wand2 className="h-3.5 w-3.5" />
											Gerar senha forte
										</Button>

										{/* Lista de regras */}
										<div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-[11px]">
											{regraItems.map((r) => (
												<div
													key={r.k}
													className={cn(
														"inline-flex items-center gap-1.5 rounded-md px-2 py-1",
														r.ok
															? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
															: "bg-slate-50 text-slate-500 dark:bg-slate-900/40 dark:text-slate-400"
													)}
												>
													{r.ok ? (
														<CheckCircle2 className="h-3 w-3 shrink-0" />
													) : (
														<AlertCircle className="h-3 w-3 shrink-0 opacity-60" />
													)}
													<span>{r.label}</span>
												</div>
											))}
										</div>

										<div className="space-y-1.5 pt-1">
											<Label htmlFor="reset-senha-confirma">
												Confirmar nova senha
											</Label>
											<div className="relative">
												<Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
												<Input
													id="reset-senha-confirma"
													type={showConfirmarSenha ? "text" : "password"}
													autoComplete="new-password"
													minLength={REGRA_SENHA.minLength}
													maxLength={REGRA_SENHA.maxLength}
													value={confirmarSenhaReset}
													onChange={(e) =>
														setConfirmarSenhaReset(e.target.value)
													}
													placeholder="Repita a senha acima"
													className="h-11 pl-10 pr-11"
													onKeyDown={(e) => {
														if (e.key === "Enter") salvarNovaSenha();
													}}
												/>
												<button
													type="button"
													tabIndex={-1}
													aria-label={
														showConfirmarSenha
															? "Esconder a senha"
															: "Mostrar a senha"
													}
													onClick={() => setShowConfirmarSenha((v) => !v)}
													className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
												>
													{showConfirmarSenha ? (
														<EyeOff className="h-4 w-4" />
													) : (
														<Eye className="h-4 w-4" />
													)}
												</button>
											</div>

											{(confere || naoConfere) && (
												<div
													className={cn(
														"inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium",
														confere
															? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
															: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300"
													)}
												>
													{confere ? (
														<CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
													) : (
														<AlertCircle className="h-3.5 w-3.5 shrink-0" />
													)}
													{confere
														? "Senhas coincidem."
														: "As senhas não coincidem."}
												</div>
											)}
										</div>
									</div>
								);
							})()
						)}
                                                        </>
                                                )}
					</div>

					<DialogFooter className="border-t bg-muted/40 px-6 py-3 sm:justify-between">
                                                <div>
                                                        {!manutencaoAtiva && etapa > 1 && (
								<Button
									type="button"
									variant="ghost"
									disabled={enviandoReset}
									onClick={() => {
										setMsgReset({ tipo: "", texto: "" });
										setEtapa((e) => Math.max(1, e - 1));
									}}
									className="gap-1"
								>
									<ArrowLeft className="h-4 w-4" />
									Voltar
								</Button>
							)}
						</div>
						<div className="flex gap-2">
                                                        <Button
                                                                type="button"
                                                                variant="ghost"
                                                                onClick={fecharModal}
                                                                disabled={enviandoReset}
                                                        >
                                                                {manutencaoAtiva ? "Fechar" : "Cancelar"}
                                                        </Button>
                                                        {!manutencaoAtiva && (
                                                                <Button
                                                                        type="button"
                                                                        disabled={enviandoReset}
                                                                        onClick={() => {
                                                                                if (etapa === 1) return pedirCodigo();
                                                                                if (etapa === 2) return confirmarCodigo();
                                                                                return salvarNovaSenha();
                                                                        }}
                                                                        className="min-w-[140px] gap-2"
                                                                >
                                                                        {enviandoReset ? (
                                                                                <>
                                                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                                                        Aguarde…
                                                                                </>
                                                                        ) : etapa === 1 ? (
                                                                                <>
                                                                                        <Mail className="h-4 w-4" />
                                                                                        Enviar código
                                                                                </>
                                                                        ) : etapa === 2 ? (
                                                                                <>
                                                                                        <ShieldCheck className="h-4 w-4" />
                                                                                        Confirmar código
                                                                                </>
                                                                        ) : (
                                                                                <>
                                                                                        <Lock className="h-4 w-4" />
                                                                                        Salvar senha
                                                                                </>
                                                                        )}
                                                                </Button>
                                                        )}
						</div>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default Login;
